import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Trophy, Calendar, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { competitionLogo } from "@/lib/competitionLogo";

const CompetitionsPage = () => {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        const response = await axios.get(`${API}/competitions`);
        setCompetitions(response.data);
      } catch (error) {
        console.error("Error fetching competitions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompetitions();
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-[#009E60] text-white",
      upcoming: "bg-[#FFC72C] text-black",
      completed: "bg-gray-500 text-white"
    };
    return (
      <span className={`px-3 py-1 text-xs font-bold uppercase ${styles[status] || styles.upcoming}`}>
        {status}
      </span>
    );
  };

  return (
    <div data-testid="competitions-page">
      {/* Hero Section */}
      <section className="bg-[#09090b] py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <Trophy className="w-16 h-16 text-[#FFC72C] mx-auto mb-6" />
          <h1 className="font-heading text-5xl md:text-6xl font-bold uppercase text-white mb-4">
            Competitions
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Explore all ABFA-sanctioned football competitions including leagues, cups, and tournaments.
          </p>
        </div>
      </section>

      {/* Competitions List */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="space-y-4">
                  <Skeleton className="h-48 w-full" />
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          ) : competitions.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {competitions.map((comp) => (
                <Link 
                  key={comp.competition_id} 
                  to={`/competitions/${comp.competition_id}`}
                  className="card-abfa group"
                  data-testid={`competition-card-${comp.competition_id}`}
                >
                  <div className="aspect-video bg-gradient-to-br from-[#09090b] to-gray-800 flex items-center justify-center relative overflow-hidden">
                    <Trophy className="w-24 h-24 text-[#FFC72C]/20 absolute" />
                    <div className="relative z-10 text-center">
                      {competitionLogo(comp)
                        ? <img src={competitionLogo(comp)} alt={`${comp.name} logo`} className="h-24 w-auto mx-auto mb-2 object-contain" data-testid={`competition-logo-${comp.competition_id}`} />
                        : <Trophy className="w-12 h-12 text-[#FFC72C] mx-auto mb-2" />}
                      <p className="text-white font-heading font-bold text-xl uppercase">{comp.short_name}</p>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-heading font-bold text-xl uppercase text-[#09090b] group-hover:text-[#009E60] transition-colors">
                        {comp.name}
                      </h3>
                      {getStatusBadge(comp.status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {comp.season}
                      </span>
                      <span className="capitalize">{comp.type}</span>
                    </div>
                    {comp.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{comp.description}</p>
                    )}
                    <div className="flex items-center text-[#009E60] font-medium text-sm group-hover:translate-x-2 transition-transform">
                      View Competition <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading font-bold text-2xl text-gray-400 uppercase mb-2">No Competitions Yet</h3>
              <p className="text-gray-500">Check back soon for upcoming competitions.</p>
            </div>
          )}
        </div>
      </section>

      {/* Competition Types Info */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl font-bold uppercase text-center text-[#09090b] mb-12">
            Competition Types
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "League", desc: "Round-robin format where all teams play each other home and away. Points determine final standings." },
              { title: "Cup", desc: "Knockout tournament format with elimination rounds leading to a final match." },
              { title: "Friendly", desc: "Non-competitive matches for team building and international exposure." },
            ].map((type, i) => (
              <div key={i} className="p-6 bg-white border-l-4 border-[#FFC72C]" data-testid={`comp-type-${i}`}>
                <h3 className="font-heading font-bold text-xl uppercase text-[#09090b] mb-3">{type.title}</h3>
                <p className="text-gray-600">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompetitionsPage;
